// Which questions this form asks, in what order, and which cannot be skipped.
//
// THIS IS THE CREATE SCREEN'S SHAPE. SPEC.md §9 confirmed multiple forms are in use and
// each presents a different set of fields, and that the requirement is PER-FORM rather
// than per-field: "a serial number can be mandatory on the hardware form and optional
// elsewhere". Both halves live in `ticket_form_fields`, and until now neither had an
// editor — forms could be created, field definitions could be created, and nothing could
// put one on the other.
//
// ORDER IS EDITED HERE BECAUSE IT IS A REAL SETTING. `listTicketForms` renders in
// `ff.sort_order`, so this is literally the top-to-bottom order an agent works through
// with a customer on the phone. Saved as position-in-the-list rather than typed numbers:
// two fields on the same number break the tie on label, which is stable, arbitrary, and
// not what anybody chose.
//
// EVERY SENTENCE COMES FROM ../../pages/admin/form-fields.js, which is pure and tested
// (test/web/form-fields.test.js). Nothing here decides what a field state means: there is
// no DOM harness in this repository, so a ternary written inline is one nobody can hold
// to being total, and a state that fell through it would render a blank cell beside a
// field name. A blank cell reads as "fine".
//
// SAVED EXPLICITLY, unlike QueueMembers beside it, which writes on every toggle. Three
// things are being edited at once here and a reorder is a sequence of moves rather than
// one decision — writing on each would put the form through orders nobody intended, and
// each of those is a live change to what the desk collects.

import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';
import {
  availableFields, move, addField, removeField, setRequired,
  fieldsPayload, hasChanges, describeField, summarise,
} from '../../pages/admin/form-fields.js';

export default function FormFields({ formId }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState([]);
  const [all, setAll] = useState([]);
  const [picked, setPicked] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback((signal) => Promise.all([
    apiFetch(`/admin/ticket-forms/${encodeURIComponent(formId)}/fields`, { signal }),
    // Every definition, including retired ones: `availableFields` drops the retired from
    // what can be ADDED, while a retired field already attached still has to be listed
    // so it can be taken off.
    apiFetch('/admin/custom-fields', { signal }),
  ]).then(([attached, defs]) => {
    setSaved(attached.data);
    setDraft(attached.data);
    setAll(defs.data);
  }), [formId]);

  useEffect(() => {
    const controller = new AbortController();
    setSaved(null);
    setError(null);
    load(controller.signal)
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [formId, load]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = await apiFetch(`/admin/ticket-forms/${encodeURIComponent(formId)}/fields`, {
        method: 'PUT',
        body: { fields: fieldsPayload(draft) },
      });
      // The response is the list as it now stands, so there is no window in which the
      // screen and the server disagree about what was just saved.
      setSaved(body.data);
      setDraft(body.data);
    } catch (err) {
      setError(consoleErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (error && saved === null) {
    return (
      <section className="mt-6 rounded-lg border border-line bg-surface p-4">
        <h2 className="text-sm font-semibold text-ink">Fields on this form</h2>
        <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
      </section>
    );
  }

  if (saved === null) {
    return (
      <section className="mt-6 rounded-lg border border-line bg-surface p-4">
        <h2 className="text-sm font-semibold text-ink">Fields on this form</h2>
        <p className="mt-2 text-sm text-ink-muted">Loading…</p>
      </section>
    );
  }

  const canAdd = availableFields(all, draft);
  const dirty = hasChanges(saved, draft);

  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">Fields on this form</h2>
        <p className="text-sm text-ink-muted">{summarise(draft)}</p>
      </div>

      <p className="mt-1 text-xs text-ink-muted">
        This is what an agent is asked on the new-ticket screen when they pick this form,
        in this order. Required means they cannot submit without it.
      </p>

      {draft.length > 0 && (
        <ul className="mt-3 divide-y divide-line rounded-lg border border-line">
          {draft.map((field, index) => {
            const note = describeField(field);
            return (
              <li key={field.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <div className="flex shrink-0 flex-col">
                  <button
                    type="button"
                    className="px-1 text-xs text-ink-muted hover:text-ink disabled:opacity-30"
                    disabled={index === 0}
                    aria-label={`Move ${field.label} up`}
                    onClick={() => setDraft((d) => move(d, index, -1))}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="px-1 text-xs text-ink-muted hover:text-ink disabled:opacity-30"
                    disabled={index === draft.length - 1}
                    aria-label={`Move ${field.label} down`}
                    onClick={() => setDraft((d) => move(d, index, 1))}
                  >
                    ▼
                  </button>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">
                    {field.label}
                    <span className="ml-2 font-mono text-xs text-ink-muted">{field.key}</span>
                  </p>
                  <p className={`text-xs ${note.ok ? 'text-ink-muted' : 'text-amber-700 dark:text-amber-400'}`}>
                    {note.text}
                  </p>
                </div>

                <label className="flex shrink-0 items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="size-4 rounded accent-brand-600"
                    checked={field.is_required === true}
                    onChange={(e) => setDraft((d) => setRequired(d, field.id, e.target.checked))}
                  />
                  Required
                </label>

                <button
                  type="button"
                  className="shrink-0 text-xs text-ink-muted hover:text-red-700 dark:hover:text-red-300"
                  onClick={() => setDraft((d) => removeField(d, field.id))}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select
          className="field-input max-w-xs"
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
        >
          <option value="">
            {canAdd.length > 0 ? 'Add a field…' : 'Every active field is already on this form'}
          </option>
          {canAdd.map((field) => (
            <option key={field.id} value={field.id}>{field.label}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn-ghost ring-1 ring-line-strong"
          disabled={!picked}
          onClick={() => {
            const field = canAdd.find((c) => String(c.id) === picked);
            if (field) setDraft((d) => addField(d, field));
            setPicked('');
          }}
        >
          Add
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>}

      <div className="mt-4 flex items-center gap-2 border-t border-line pt-3">
        <button type="button" className="btn-primary" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save fields'}
        </button>
        <button
          type="button"
          className="btn-ghost ring-1 ring-line-strong"
          disabled={!dirty || saving}
          onClick={() => { setDraft(saved); setError(null); }}
        >
          Discard
        </button>
        {dirty && (
          <span className="text-xs text-ink-muted">
            Not saved yet. The create screen still shows the previous list.
          </span>
        )}
      </div>
    </section>
  );
}
